import { LIVE_SPORT_IDs } from 'config/constants';
import { UserContext } from 'context/userContext';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { axios } from 'setup/axiosSetup';
import {
  createWebSocket,
  subscribePrematchSports,
  unsubscribePrematchSports,
} from 'services/webSocketService';

/**
 * One refresh for the pages. `sportIds` are all the sports with new prematch
 * data since the last refresh; `sportId` is the latest of them.
 */
export type PrematchPush = {
  sportId: number;
  sportIds: number[];
  version: number;
};

/**
 * The sync finishes a prematch round for some sport several times a minute;
 * an open page refetches at most this often, with the latest push.
 */
const PREMATCH_REFRESH_MIN_MS = 15_000;

export const PrematchCatalogContext = React.createContext<{
  lastPrematchPush: PrematchPush | null;
}>(null as any);

interface Props {
  children: React.ReactNode;
}

const PrematchCatalogProvider = ({ children }: Props) => {
  const { webSocket } = useContext(UserContext);
  const [guestWebSocket, setGuestWebSocket] = useState<WebSocket | null>(null);
  const [lastPrematchPush, setLastPrematchPush] = useState<PrematchPush | null>(
    null
  );
  const lastPrematchVersionRef = useRef<Map<number, number>>(new Map());
  const lastEmitRef = useRef(0);
  const pendingPushRef = useRef<PrematchPush | null>(null);
  const emitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Hands a push to the pages, after dropping the browser's cached answers:
  // they are kept for a minute and the refetch must see the new offer.
  const emitPush = () => {
    emitTimerRef.current = null;
    const push = pendingPushRef.current;
    if (!push) return;
    pendingPushRef.current = null;
    lastEmitRef.current = Date.now();
    try {
      void axios.storage.clear?.();
    } catch {
      // the cache is a convenience; a refetch without it is fine
    }
    setLastPrematchPush(push);
  };

  useEffect(
    () => () => {
      if (emitTimerRef.current) clearTimeout(emitTimerRef.current);
    },
    []
  );

  const pathname = useLocation().pathname;
  const prematchCatalogRoutes =
    pathname.startsWith('/sports-betting') ||
    pathname.startsWith('/live-betting');

  const activeWebSocket = webSocket ?? guestWebSocket;

  useEffect(() => {
    if (!prematchCatalogRoutes || webSocket || guestWebSocket) {
      return;
    }
    const socket = createWebSocket();
    setGuestWebSocket(socket);
  }, [prematchCatalogRoutes, webSocket, guestWebSocket]);

  useEffect(() => {
    if (prematchCatalogRoutes && !webSocket) {
      return;
    }
    if (guestWebSocket) {
      guestWebSocket.close();
      setGuestWebSocket(null);
    }
  }, [prematchCatalogRoutes, webSocket, guestWebSocket]);

  useEffect(() => {
    if (!prematchCatalogRoutes || !activeWebSocket) {
      return;
    }

    const subscribe = () => {
      subscribePrematchSports(activeWebSocket, LIVE_SPORT_IDs);
    };
    const unsubscribe = () => {
      unsubscribePrematchSports(activeWebSocket, LIVE_SPORT_IDs);
    };

    if (activeWebSocket.readyState === WebSocket.OPEN) {
      subscribe();
    } else {
      activeWebSocket.addEventListener('open', subscribe);
    }

    const onMessage = (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        if (
          data.type !== 'prematch_update' ||
          typeof data.sport_id !== 'number'
        ) {
          return;
        }
        const version = Number(data.version ?? 0);
        const lastVersion =
          lastPrematchVersionRef.current.get(data.sport_id) ??
          Number.NEGATIVE_INFINITY;
        if (version <= lastVersion) {
          return;
        }
        lastPrematchVersionRef.current.set(data.sport_id, version);
        const pendingSports = pendingPushRef.current?.sportIds ?? [];
        pendingPushRef.current = {
          sportId: data.sport_id,
          sportIds: pendingSports.includes(data.sport_id)
            ? pendingSports
            : [...pendingSports, data.sport_id],
          version,
        };
        if (emitTimerRef.current) return;
        const wait = lastEmitRef.current + PREMATCH_REFRESH_MIN_MS - Date.now();
        if (wait <= 0) {
          emitPush();
        } else {
          emitTimerRef.current = setTimeout(emitPush, wait);
        }
      } catch {
        // ignore malformed payloads
      }
    };

    activeWebSocket.addEventListener('message', onMessage);

    return () => {
      activeWebSocket.removeEventListener('message', onMessage);
      activeWebSocket.removeEventListener('open', subscribe);
      if (activeWebSocket.readyState === WebSocket.OPEN) {
        unsubscribe();
      }
    };
  }, [activeWebSocket, prematchCatalogRoutes]);

  return (
    <PrematchCatalogContext.Provider value={{ lastPrematchPush }}>
      {children}
    </PrematchCatalogContext.Provider>
  );
};

export default PrematchCatalogProvider;
